import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { OFFICE, openTriage } from "../data/office";
import LawyerCarousel from "./LawyerCarousel";
import SocialIcons from "./SocialIcons";

const LINES = ["Seus direitos,", "defendidos com", "técnica e clareza."];

const line = {
  hidden: { y: "110%", rotate: 2 },
  show: (i) => ({
    y: "0%",
    rotate: 0,
    transition: { duration: 1, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yPhoto = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="grain relative flex min-h-screen items-center overflow-hidden bg-navy-900 pt-[72px]"
    >
      <motion.div
        style={{ y: yGlow }}
        className="pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-gold-500/10 blur-[140px]"
      />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-navy-950 to-transparent" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-5 py-16 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-10">
        <div>
          <motion.img
            data-testid="hero-logo"
            src="/logo-amn.png"
            alt="AMN Advocacia"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="mx-auto mb-12 h-auto w-60 sm:w-72 lg:-mt-16 lg:w-[21rem]"
            draggable="false"
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="font-mono-meta mb-7 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] uppercase tracking-[0.35em] text-gold-400"
          >
            <span>Consultoria jurídica</span>
            <span className="inline-block h-1 w-1 rotate-45 bg-gold-500" />
            <span className="text-mist">{OFFICE.oab}</span>
          </motion.p>

          <h1 className="font-display text-[13vw] font-medium leading-[0.98] text-cream sm:text-7xl lg:text-[4.6rem] xl:text-[5.2rem]">
            {LINES.map((text, i) => (
              <span key={text} className="block overflow-hidden pb-1">
                <motion.span
                  data-testid={`hero-line-${i}`}
                  className="block"
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="show"
                >
                  {i === LINES.length - 1 ? (
                    <>
                      <em className="gold-text italic">{text}</em>
                    </>
                  ) : (
                    text
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-mist sm:text-lg"
          >
            AMN Advogadas Associadas — atendimento humanizado, estratégico e
            dedicado à defesa dos seus direitos. Encontre o seu problema abaixo
            e fale conosco pelo WhatsApp em segundos.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-whatsapp"
              onClick={() => openTriage()}
              className="group relative overflow-hidden rounded-full bg-gold-500 px-8 py-4 text-sm font-bold uppercase tracking-widest text-navy-950 transition-transform duration-300 hover:scale-[1.03]"
            >
              <span className="relative z-10">Agendar consulta</span>
              <span className="absolute inset-0 -translate-x-full bg-gold-300 transition-transform duration-500 group-hover:translate-x-0" />
            </button>
            <button
              data-testid="hero-cta-areas"
              onClick={() => {
                const el = document.querySelector("#areas");
                if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
                else el.scrollIntoView({ behavior: "smooth" });
              }}
              className="group flex items-center gap-2 rounded-full border border-navy-600 px-8 py-4 text-sm font-semibold text-cream transition-colors duration-300 hover:border-gold-500/60 hover:text-gold-400"
            >
              Áreas de atuação
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-3 text-xs uppercase tracking-[0.2em] text-mist"
          >
            <span className="flex items-center gap-2">
              <span className="h-1 w-1 rotate-45 bg-gold-500" />
              Atendimento presencial e online
            </span>
            <span className="flex items-center gap-2">
              <span className="h-1 w-1 rotate-45 bg-gold-500" />
              Resposta rápida pelo WhatsApp
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.45 }}
          >
            <SocialIcons className="mt-8" />
          </motion.div>
        </div>

        <motion.div style={{ y: yPhoto }} className="relative mx-auto w-full max-w-md lg:max-w-none">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 rounded-2xl border border-gold-500/30" />
            <div className="relative aspect-[4/5] overflow-hidden rounded-2xl">
              <LawyerCarousel />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
